/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useKonami } from "react-konami-code";
import { css } from "@panda/css";

const EASTER_EGG_VIDEO_URL = "https://cms-assets.unrealengine.com/AiKUh5PQCTaOFnmJDZJBfz/cmfe7cg2y0j1m07n7mqgp6yo7";

const styles = css({
	position: "fixed",
	top: 0,
	left: 0,
	width: "100vw",
	height: "100vh",
	backgroundColor: "black",
	zIndex: 99999,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
});

export interface UnrealKonamiProps {
	videoUrl?: string | null;
}

/**
 * UnrealKonami - Easter egg component that displays a full-screen video
 * when the Konami Code (↑↑↓↓←→←→BA) is entered
 *
 * @returns React component that renders a modal with video when activated
 */
export const UnrealKonami = ({ videoUrl }: UnrealKonamiProps): React.JSX.Element | null => {
	const [isActive, setIsActive] = useState(false);

	const dialogRef = useRef<HTMLDivElement>(null);
	const previousActiveElement = useRef<Element | null>(null);

	useEffect(() => {
		if (isActive) {
			// Store previous focus
			previousActiveElement.current = document.activeElement;
			// Focus the dialog
			dialogRef.current?.focus();

			// Trap focus within modal
			const handleTab = (e: KeyboardEvent): void => {
				// Focus trapping logic
				if (e.key === "Tab") {
					e.preventDefault();
					dialogRef.current?.focus();
				}
			};
			document.addEventListener("keydown", handleTab);

			return () => {
				document.removeEventListener("keydown", handleTab);
				// Restore focus
				(previousActiveElement.current as HTMLElement)?.focus();
			};
		}
		return undefined;
	}, [isActive]);

	const handleOpen = useCallback((): void => {
		setIsActive(true);
	}, []);

	useKonami(handleOpen);

	const handleClose = useCallback((): void => {
		setIsActive(false);
	}, []);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent): void => {
			if (event.key === "Escape") {
				handleClose();
			}
		},
		[handleClose],
	);

	const src = videoUrl || EASTER_EGG_VIDEO_URL;

	if (!isActive) return null;

	return (
		// eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
		<div
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			className={styles}
			tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
			role="dialog"
			aria-modal="true"
			aria-label="Konami Code Easter Egg Video - Press Escape to close"
		>
			<video
				key={src}
				src={src}
				preload="none"
				autoPlay
				muted
				loop
				className={css({
					maxWidth: "100%",
					maxHeight: "100%",
					objectFit: "contain",
				})}
			/>
		</div>
	);
};
